/**
 * Login screen and session bootstrap.
 *
 * Flow:
 *   1. App load: bootstrap() calls /api/auth/me. If 200 → user is logged in,
 *      we have csrf_token, hide login screen.
 *   2. If 401 → show login screen.
 *   3. On submit: call /api/auth/login. On success, hide login screen and
 *      kick off the rest of the app (map list load).
 *   4. Logout button: clear session, show login again.
 */

import * as api from './api.js';
import { ApiError } from './types.js';
import { setState } from './state.js';

interface LoginScreenElements {
  root: HTMLElement;
  form: HTMLFormElement;
  emailInput: HTMLInputElement;
  passwordInput: HTMLInputElement;
  errorBox: HTMLElement;
  submitButton: HTMLButtonElement;
}

let loginEls: LoginScreenElements | null = null;

export function mountLoginScreen(container: HTMLElement, onSuccess: () => void): void {
  container.innerHTML = `
    <div class="login-screen" role="dialog" aria-labelledby="login-title">
      <form class="login-form" novalidate>
        <h1 id="login-title">mapnmove</h1>
        <p class="login-subtitle">Sign in to continue</p>
        <label>
          <span>Email</span>
          <input type="email" name="email" required autocomplete="username" />
        </label>
        <label>
          <span>Password</span>
          <input type="password" name="password" required autocomplete="current-password" />
        </label>
        <div class="login-error" role="alert" aria-live="polite"></div>
        <button type="submit">Sign in</button>
      </form>
    </div>
  `;

  const form = container.querySelector<HTMLFormElement>('.login-form');
  const emailInput = container.querySelector<HTMLInputElement>('input[name=email]');
  const passwordInput = container.querySelector<HTMLInputElement>('input[name=password]');
  const errorBox = container.querySelector<HTMLElement>('.login-error');
  const submitButton = container.querySelector<HTMLButtonElement>('button[type=submit]');

  if (!form || !emailInput || !passwordInput || !errorBox || !submitButton) {
    throw new Error('Login screen failed to mount');
  }

  loginEls = { root: container, form, emailInput, passwordInput, errorBox, submitButton };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    await handleLogin(onSuccess);
  });
}

async function handleLogin(onSuccess: () => void): Promise<void> {
  if (loginEls === null) return;
  const { emailInput, passwordInput, errorBox, submitButton } = loginEls;

  errorBox.textContent = '';
  submitButton.disabled = true;
  try {
    const { user, csrf_token } = await api.login(emailInput.value, passwordInput.value);
    setState({ user, csrfToken: csrf_token });
    hideLoginScreen();
    onSuccess();
  } catch (err) {
    if (err instanceof ApiError) {
      errorBox.textContent = err.status === 401 ? 'Invalid email or password' : err.message;
    } else {
      errorBox.textContent = 'Sign-in failed. Please try again.';
      console.error(err);
    }
  } finally {
    submitButton.disabled = false;
  }
}

export function showLoginScreen(): void {
  if (loginEls === null) return;
  loginEls.root.hidden = false;
  loginEls.emailInput.value = '';
  loginEls.passwordInput.value = '';
  loginEls.errorBox.textContent = '';
  loginEls.emailInput.focus();
}

export function hideLoginScreen(): void {
  if (loginEls === null) return;
  loginEls.root.hidden = true;
}

/**
 * Probe the session at app start. Resolves to true if logged in, false if not.
 * Any other error is surfaced (network down, 500, etc.) — caller decides UX.
 */
export async function bootstrapSession(): Promise<boolean> {
  try {
    const { user, csrf_token } = await api.getMe();
    setState({ user, csrfToken: csrf_token });
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return false;
    }
    throw err;
  }
}

export async function performLogout(): Promise<void> {
  await api.logout();
  setState({ user: null, csrfToken: null, currentMap: null, tokens: [], maps: [] });
  showLoginScreen();
}
